import type { InboxItem } from "./types";

// Rows the phone writes into the bell (#294), beside the server's. They live
// on this phone only: the card says so ("This phone"). Each kind brings its
// own actions; a host runs them (apps/mobile/src/bell/useLocalBell.ts).

export type LocalBellKind =
  | "hcNoConsent"
  | "hcRefusal"
  | "hcDrift"
  | "hcUpdate"
  | "stuckWrites"
  | "draftResume"
  | "workoutRecovery"
  | "autoSaved"
  | "profileName";

/** Where a row's "Open" or "Details" goes: a page, or the stuck-writes
 *  sheet (#295). #288: `workouts` is the unsaved workout, back in the
 *  logger; `workoutMerge` the Workouts list, asking which same-day workout
 *  a recovered one joins. #445: `workoutEdit` a saved workout, in the editor;
 *  `workoutDraft` the draft kept in the live slot, back in the logger. */
export type BellTarget =
  | { to: "healthConnect" }
  | { to: "settingsMeasurements" }
  | { to: "settingsAccount" }
  | { to: "stuckWrites" }
  | { to: "workouts" }
  | { to: "workoutMerge"; slotKey: string }
  | { to: "workoutEdit"; sessionId: string }
  | { to: "workoutDraft" };

/** Label keys: InboxSection's LOCAL_ACTION_LABEL. "details" opens the
 *  target too. */
export type LocalActionId =
  | "open"
  | "hide"
  | "close"
  | "retry"
  | "details"
  | "resume"
  | "discard"
  | "saveAsNew"
  | "merge"
  | "sendSupport";

export type LocalBellRow = {
  origin: "local";
  /** Stable per row family; also the Android notification's id. */
  id: string;
  /** Changes with the content: a new version posts again and undoes a hide. */
  version: string;
  kind: LocalBellKind;
  createdAt: string;
  titleKey: string;
  bodyKey: string;
  /** A number stays a number: `count` picks the plural form. */
  params?: Record<string, string | number>;
  /** Also an Android notification. */
  urgent: boolean;
  /** Where "Open" and a tap on the notification lead. Null: the tap only
   *  brings the app up, and the row waits in the bell (#288). */
  target: BellTarget | null;
  actions: readonly LocalActionId[];
  /** A server row this one stands in for, hidden while this one shows: the
   *  same news, told here where it can be acted on (#288). */
  supersedes?: string;
};

export type BellItem = InboxItem | LocalBellRow;

export const isLocalBellRow = (item: BellItem): item is LocalBellRow =>
  "origin" in item && item.origin === "local";

/** What a host hands the provider: its rows and how to act on them. */
export type LocalBell = {
  rows: readonly LocalBellRow[];
  /** True when the action went through. */
  run: (row: LocalBellRow, action: LocalActionId) => Promise<boolean>;
  busyId: string | null;
  /** The last action that failed, cleared by the next one. */
  error: Error | null;
};

/** One list and one count: the phone's rows first, newest first. A server
 *  row a phone row supersedes is left out, and out of the count too. */
export function mergeBell(
  serverItems: readonly InboxItem[] | null,
  serverCount: number,
  local: readonly LocalBellRow[],
): { items: BellItem[] | null; count: number } {
  const mine = [...local].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  if (serverItems === null && mine.length === 0) {
    return { items: null, count: serverCount };
  }
  const gone = new Set(mine.flatMap((r) => (r.supersedes ? [r.supersedes] : [])));
  const server = (serverItems ?? []).filter((it) => !gone.has(it.id));
  const hidden = (serverItems ?? []).length - server.length;
  return {
    items: [...mine, ...server],
    count: mine.length + Math.max(0, serverCount - hidden),
  };
}
